import { createContext, useContext } from 'react';

export interface FeatureFlagsProviderContextProps<T = unknown> {
  /**
   * `true` immediately when the provider uses `async`, or after the first successful
   * `identify` callback otherwise. For user-targeted flags, prefer `isUserContextIdentified`.
   */
  ready: boolean;
  /**
   * `true` only after the **latest** LaunchDarkly `identify` kicked off by `setContext` has
   * completed (completion callback). Resets to `false` when a new `identify` starts so
   * consumers do not treat org/user-targeted `getFlag` values as trustworthy across context
   * changes until that identify finishes. Stale completion callbacks are ignored if a newer
   * `identify` was started in the meantime. While the LD client is not yet available, a new
   * `setContext` sets this to `false` until the queued context is flushed and identify completes.
   */
  isUserContextIdentified: boolean;
  /**
   * Updates user/org context for targeting. Valid calls are stored as the latest pending
   * context; when the LaunchDarkly client exists, `identify` runs (immediately or on the next
   * effect after the client mounts). Do not dedupe in app code by updating refs before calling
   * this — the resolver queues until the client is ready.
   */
  setContext: (name: string, value: T) => void;
}

const FeatureFlagsProviderContext =
  createContext<FeatureFlagsProviderContextProps>({
    ready: false,
    isUserContextIdentified: false,
    setContext: () => {},
  });

export const useFeatureFlagsProviderContext = () =>
  useContext(FeatureFlagsProviderContext);
export default FeatureFlagsProviderContext;
